import { model } from './firebase.js'; // adjust path if needed
import mammoth from 'mammoth';

// -------------------- helpers --------------------
async function fileToBase64Str(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const dataUrl = reader.result || ''; // "data:<mime>;base64,AAAA..."
      const commaIdx = dataUrl.indexOf(',');
      const base64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
      resolve({ base64, mimeType: file.type || 'image/jpeg' });
    };
    reader.readAsDataURL(file);
  });
}

async function extractTextFromDocx(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = async (event) => {
      try {
        const arrayBuffer = event.target.result;
        const result = await mammoth.extractRawText({ arrayBuffer });
        resolve(result.value); // The raw text
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

function parseLooseJSON(text) {
  if (!text || typeof text !== 'string') return null;
  // strip triple-fence
  text = text.replace(/^\s*```(?:json)?/i, '').replace(/```\s*$/i, '').trim();
  try { return JSON.parse(text); } catch { /* fallthrough */ }

  // try to find first balanced {...}
  const firstOpen = text.indexOf('{');
  if (firstOpen === -1) return null;
  let depth = 0;
  for (let i = firstOpen; i < text.length; i++) {
    const ch = text[i];
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) {
        const candidate = text.slice(firstOpen, i + 1);
        try { return JSON.parse(candidate); } catch { break; }
      }
    }
  }
  return null;
}

const clampInt = (v, min = 0, max = 100) => {
  const n = Number.isFinite(Number(v)) ? Math.round(Number(v)) : null;
  if (n === null) return null;
  return Math.max(min, Math.min(max, n));
};

function normalizeToTargetSum(vals, targetSum = 100) {
  const totalRaw = (vals || []).reduce((s, v) => s + (Number.isFinite(Number(v)) ? Number(v) : 0), 0);
  const count = vals.length || 0;
  if (count === 0) return [];
  if (totalRaw === 0) {
    const base = Math.floor(targetSum / count);
    const out = Array(count).fill(base);
    let rem = targetSum - base * count;
    let i = 0;
    while (rem > 0) {
      out[i % count] += 1;
      rem -= 1;
      i += 1;
    }
    return out;
  }
  const scaled = vals.map(v => (Number.isFinite(Number(v)) ? (v / totalRaw) * targetSum : 0));
  const floored = scaled.map(v => Math.floor(v));
  let sumFloor = floored.reduce((s, x) => s + x, 0);
  const fractions = scaled.map((v, i) => ({ idx: i, frac: v - Math.floor(v) }));
  fractions.sort((a, b) => b.frac - a.frac);
  let rem = targetSum - sumFloor;
  let j = 0;
  while (rem > 0 && j < fractions.length) {
    floored[fractions[j].idx] += 1;
    rem -= 1;
    j += 1;
  }
  return floored.map(x => Math.max(0, Math.min(100, Math.round(x))));
}

function roundToStepWithSumNormalized(arr, targetSum = 100, step = 5) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const s = arr.reduce((a, b) => a + (Number.isFinite(Number(b)) ? Number(b) : 0), 0);
  if (s === 0) return Array(arr.length).fill(0);
  const actualStep = (typeof step === 'number' && step > 1) ? step : 1;
  const rounded = arr.map(v => {
    const n = Number.isFinite(Number(v)) ? Number(v) : 0;
    return Math.max(0, Math.min(100, Math.round(n / actualStep) * actualStep));
  });
  let sumRounded = rounded.reduce((a, b) => a + b, 0);
  let diff = targetSum - sumRounded;
  const residues = arr.map((v, i) => {
    const n = Number.isFinite(Number(v)) ? Number(v) : 0;
    const frac = n - Math.round(n / actualStep) * actualStep;
    return { idx: i, frac, original: n, rounded: rounded[i] };
  });
  while (diff !== 0) {
    if (diff > 0) {
      residues.sort((a, b) => {
        const aDiff = (a.original - a.rounded);
        const bDiff = (b.original - b.rounded);
        if (aDiff === bDiff) return a.rounded - b.rounded;
        return bDiff - aDiff;
      });
      const cand = residues.find(r => rounded[r.idx] + actualStep <= 100);
      if (!cand) break;
      rounded[cand.idx] += actualStep;
      sumRounded += actualStep;
      diff -= actualStep;
    } else {
      residues.sort((a, b) => {
        if (a.rounded === b.rounded) return (a.original - a.rounded) - (b.original - b.rounded);
        return b.rounded - a.rounded;
      });
      const cand = residues.find(r => rounded[r.idx] - actualStep >= 0);
      if (!cand) break;
      rounded[cand.idx] -= actualStep;
      sumRounded -= actualStep;
      diff += actualStep;
    }
  }
  let finalSum = rounded.reduce((a, b) => a + b, 0);
  let rem = targetSum - finalSum;
  let i = 0;
  while (rem !== 0 && i < rounded.length * 2) {
    const idx = i % rounded.length;
    if (rem > 0 && rounded[idx] < 100) {
      rounded[idx] += (actualStep > 1 ? actualStep : 1);
      rem -= (actualStep > 1 ? actualStep : 1);
    } else if (rem < 0 && rounded[idx] > 0) {
      rounded[idx] -= (actualStep > 1 ? actualStep : 1);
      rem += (actualStep > 1 ? actualStep : 1);
    }
    i++;
  }
  return rounded.map(v => Math.max(0, Math.min(100, Math.round(v))));
}

function inferDueDateFromLabels(labels = [], strings = '') {
  const txt = (strings || '').toLowerCase();
  const lowLabels = (labels || []).map(s => (s || '').toString().toLowerCase());
  const tokens = [...lowLabels, txt];
  const today = new Date();
  const addDays = (d) => {
    const out = new Date(today);
    out.setDate(out.getDate() + d);
    return out.toISOString().slice(0, 10);
  };
  if (tokens.some(t => /\b(daily|every day|each day)\b/.test(t))) return addDays(1);
  if (tokens.some(t => /\b(weekly|every week|each week)\b/.test(t))) return addDays(7);
  if (tokens.some(t => /\b(monthly|every month|each month)\b/.test(t))) return addDays(30);
  if (tokens.some(t => /\b(quarterly|every quarter)\b/.test(t))) return addDays(90);
  const isoMatch = strings.match(/\d{4}-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[0];
  const dmMatch = strings.match(/\b(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})\b/);
  if (dmMatch) {
    const d = Number(dmMatch[1]), m = Number(dmMatch[2]), yRaw = dmMatch[3];
    let year = Number(yRaw);
    if (year < 100) year += (year >= 70 ? 1900 : 2000);
    try {
      const candidate = new Date(year, m - 1, d);
      if (!Number.isNaN(candidate.getTime())) return candidate.toISOString().slice(0, 10);
    } catch { /* ignore */ }
  }
  return null;
}

function inferWeight(item, globalKeywords = []) {
  if (item.weight !== undefined && item.weight !== null) {
    const w = clampInt(item.weight);
    if (w !== null) return w;
  }
  if (item.effort !== undefined && item.effort !== null && Number.isFinite(Number(item.effort))) {
    const e = Math.max(1, Math.min(10, Math.round(Number(item.effort))));
    return clampInt(e * 10);
  }
  if (item.priorityScale && typeof item.priorityScale === 'string') {
    const ps = item.priorityScale.toLowerCase();
    if (ps === 'easy') return 10;
    if (ps === 'medium') return 45;
    if (ps === 'hard') return 75;
    if (ps === 'high') return 90;
  }
  const text = ((item.title || '') + ' ' + (item.description || '') + ' ' + (globalKeywords || []).join(' ')).toLowerCase();
  let score = 50;
  const easyWords = ['email', 'emails', 'responding', 'phone', 'call', 'admin', 'administration', 'routine', 'daily'];
  if (easyWords.some(w => text.includes(w))) score -= 25;
  const harderWords = ['plan', 'planning', 'prepare', 'preparation', 'research', 'training', 'evaluation', 'run an event', 'oversee', 'overseeing', 'organis(e|z)'];
  if (harderWords.some(w => text.includes(w))) score += 20;
  const veryHardWords = ['complex', 'integration', 'infrastructure', 'migration', 'audit', 'major', 'design', 'architect'];
  if (veryHardWords.some(w => text.includes(w))) score += 30;
  if (text.includes('urgent') || text.includes('asap') || text.includes('priority')) score += 15;
  if (text.includes('daily')) score -= 10;
  if (text.includes('weekly')) score -= 5;
  if (text.includes('monthly')) score += 0;
  return clampInt(Math.round(score));
}

function normalizePriority(item, globalKeywords = []) {
  if (item.priority && typeof item.priority === 'string') {
    const p = (item.priority || '').toLowerCase();
    if (['low', 'medium', 'high'].includes(p)) return p;
  }
  if (item.priorityScale && typeof item.priorityScale === 'string') {
    const ps = item.priorityScale.toLowerCase();
    if (ps === 'easy') return 'low';
    if (ps === 'medium') return 'medium';
    if (ps === 'hard' || ps === 'high') return 'high';
  }
  const text = ((item.title || '') + ' ' + (item.description || '') + ' ' + (globalKeywords || []).join(' ')).toLowerCase();
  if (text.includes('urgent') || text.includes('important')) return 'high';
  return null;
}

// keep '@' in tokens (canonicalize emails & uid tokens)
function normalizeTokenForEmail(tok = '') {
  if (!tok) return '';
  return String(tok).trim().toLowerCase().replace(/[()[\]<>,"'`]/g, '').replace(/\s+/g, '');
}

// -------------------- main exported function --------------------
export async function processImageForTasks(imageFile, details = {}) {
  const context = (details.context || '').trim();
  const candidateEmailsRaw = Array.isArray(details.candidateEmails) ? details.candidateEmails : [];
  const candidateMapRaw = (details.candidateMap && typeof details.candidateMap === 'object') ? details.candidateMap : {};
  const excludedTokensRaw = Array.isArray(details.excludedTokens) ? details.excludedTokens : [];

  // canonical candidateEmails (emails preferred; tokens normalized)
  const candidateEmails = Array.from(new Set(candidateEmailsRaw.map(e => normalizeTokenForEmail(e)).filter(Boolean)));

  // normalized candidateMap: tokenNormalized -> emailNormalized
  const candidateMap = {};
  Object.keys(candidateMapRaw || {}).forEach(k => {
    const v = candidateMapRaw[k];
    const kn = normalizeTokenForEmail(k);
    const vn = (v && typeof v === 'string') ? normalizeTokenForEmail(v) : null;
    if (kn && vn) candidateMap[kn] = vn;
  });

  // excluded normalized tokens set (owner, higher-level tokens)
  const excludedSet = new Set((excludedTokensRaw || []).map(x => normalizeTokenForEmail(x)).filter(Boolean));

  // remove excluded candidates from candidateEmails and candidateMap
  const filteredCandidateEmails = candidateEmails.filter(e => !excludedSet.has(e));
  Object.keys(candidateMap).forEach(k => {
    if (excludedSet.has(k) || excludedSet.has(candidateMap[k])) delete candidateMap[k];
  });


  // original long prompt (kept)
  const originalPrompt = `
    CONTEXT:
    ${context}

    INSTRUCTIONS (RETURN JSON ONLY):
    Analyze the provided image and return a VALID JSON object only (no explanation, no code fences). The JSON must follow this schema exactly:

    {
      "lists": [
        {
          "name": string,            // List name (e.g., "PLDT Pasig", "Frontend Tasks")
          "items": [                 // Array of tasks for this list
            {
              "title": string,
              "description": string | null,
              "startDate": string | null,
              "dueDate": string | null,
              "priorityScale": string | null,
              "priority": string | null,
              "weight": number | null,
              "assignees": [ string ],
              "labels": [ string ],
              "effort": number | null,
              "subtasks": [
                 { 
                   "text": string, 
                   "completed": boolean, 
                   "weight": number | null
                 } 
              ]
            }
          ]
        }
      ],
      "designSuggestions": string[],
      "uploadPrompt": string | null
    }

    - Structure: Identify GROUPINGS in the document. 
        *   If the document has headers like "PLDT Pasig", "PLDT Taguig", create SEPARATE lists for them.
        *   If there is only one group, return one list in the "lists" array.
    - Dates: STRICTLY extract dates.
        *   The Start and Due dates belong to the MAIN TASK. 
        *   Scan ALL lines in the task block, including lines that look like subtasks. 
        *   If a subtask line says "due - (DD/MM/YYYY)", extract that date as the MAIN TASK's "dueDate".
        *   If a line says "start - (DD/MM/YYYY)", extract it as the MAIN TASK's "startDate".
        *   Ignore the text "start" or "due" in the subtask text itself if it's purely a metadata marker.
    - Subtasks: Look for indented lines, bullet points. Group them as subtasks.
    - Metadata Lines: Any line starting with "assign" or "due" or "start" is metadata for the PARENT task.
    - Weights & Effort: 
        *   Main Tasks: If implied (e.g. "- 10%"), use it as weight. Otherwise leave null.
        *   Subtasks: If subtasks have explicit percentage or weight mentioned, use it.
    - Priority / Hardness: VERY IMPORTANT. You must evaluate the difficulty/complexity of the task based on its title and description.
        *   "high" = Complex engineering, major design, urgent timelines, or blocking infrastructure work.
        *   "medium" = Standard operational work, moderate planning, testing, or multi-step execution.
        *   "low" = Routine, simple, administrative work, OR if there is not enough detailed context to justify it being harder. 
        *   SET THIS VALUE in the "priority" field for EVERY task. Always default to "low" if unsure.
    - Assignees: Look for patterns like "assign task - [email]".
    - Labels: return any tokens like "weekly", "invoice", "meeting".

    EXAMPLES:
    - Text: "Quezon QL.1-10% start - (06/02/2026)" 
      -> title: "Quezon QL.1", weight: 10, startDate: "2026-02-06", subtasks: []
    - Text: "due - (15/02/2026)" 
      -> dueDate: "2026-02-15"
    - Text: "assign task - madagascar@gmail.com" 
      -> assignees: ["madagascar@gmail.com"]

    Return ONLY the JSON object now.
  `;

  const candidateBlock = filteredCandidateEmails.length
    ? `\nCANDIDATE_EMAILS (prefer/correct to these if OCR is noisy):\n${filteredCandidateEmails.join('\n')}\n\n`
    : '\n';
  const promptText = originalPrompt.replace(/\nReturn ONLY the JSON object now\.\s*$/, `${candidateBlock}Return ONLY the JSON object now.`);

  // build payload and call model
  let parts = [];

  if (imageFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    try {
      const docText = await extractTextFromDocx(imageFile);
      // Pass as text prompt
      parts = [{ text: promptText + "\n\n--- DOCUMENT CONTENT ---\n" + docText }];
    } catch (err) {
      console.error("Failed to parse DOCX", err);
      throw new Error("Failed to read DOCX file. Please upload a valid document.");
    }
  } else {
  // Image or PDF
    const { base64, mimeType } = await fileToBase64Str(imageFile);
    parts = [{ text: promptText }, { inlineData: { mimeType, data: base64 } }];
  }

  try {
    const result = await model.generateContent(parts, { generationConfig: { responseMimeType: 'application/json' } });

    // extract candidate text
    const candidates = result?.response?.candidates;
    let rawText = null;
    if (Array.isArray(candidates) && candidates.length > 0) {
      const cp = candidates[0]?.content?.parts || [];
      for (const p of cp) {
        if (p.text && typeof p.text === 'string') { rawText = p.text; break; }
      }
    }
    if (!rawText && typeof result.response?.text === 'function') rawText = await result.response.text();
    if (!rawText) return { raw: result };

    let parsed = parseLooseJSON(rawText);
    if (!parsed) {
      const fenceMatch = rawText.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
      if (fenceMatch) parsed = parseLooseJSON(fenceMatch[1]);
    }
    if (!parsed) return { raw: rawText };

    // Post-process parsed result - normalize items
    const globalKeywords = Array.isArray(parsed.keywords) ? parsed.keywords : [];
    
    // Normalize to new "lists" structure
    let rawLists = [];
    if (Array.isArray(parsed.lists)) {
        rawLists = parsed.lists;
    } else {
        // Migration/Fallback: map old structure to new list format
        if (!parsed.arrays) parsed.arrays = {};
        const oldDocs = Array.isArray(parsed.arrays.documentList) ? parsed.arrays.documentList : [];
        if (oldDocs.length > 0) {
            rawLists.push({
                name: parsed.listNameSuggestion || "Imported Tasks",
                items: oldDocs
            });
        }
    }

    // Process each list
    const processedLists = rawLists.map(listObj => {
        const docList = Array.isArray(listObj.items) ? listObj.items : [];
        if (docList.length === 0) return { ...listObj, items: [] };

        const intermediate = docList.map((it) => {
          const item = { ...(it || {}) };
          item.title = item.title ? String(item.title).trim() : null;
          item.description = item.description ? String(item.description).trim() : null;
          if (item.effort !== undefined && item.effort !== null && Number.isFinite(Number(item.effort))) {
            const e = Math.round(Number(item.effort));
            item.effort = Math.max(1, Math.min(10, e));
          } else item.effort = item.effort === null ? null : null;
          if (item.priorityScale && typeof item.priorityScale === 'string') {
            const ps = item.priorityScale.toLowerCase();
            item.priorityScale = ['easy', 'medium', 'hard', 'high'].includes(ps) ? ps : null;
          } else item.priorityScale = null;
          item.labels = Array.isArray(item.labels) ? item.labels.map(s => String(s).trim()).filter(Boolean) : [];
          item.assignees = Array.isArray(item.assignees) ? item.assignees.map(s => String(s).trim()).filter(Boolean) : [];

          // Normalize subtasks
          item.subtasks = Array.isArray(item.subtasks)
            ? item.subtasks.map(st => ({
              text: st.text ? String(st.text).trim() : 'Subtask',
              completed: !!st.completed,
              weight: (st.weight !== undefined && st.weight !== null && Number.isFinite(Number(st.weight)))
                ? Math.max(0, Math.min(100, Math.round(Number(st.weight))))
                : null
            }))
            : [];

          const providedDue = item.dueDate ? String(item.dueDate).trim() : null;
          let parsedDue = null;
          if (providedDue) {
            // Strip parentheses and whitespace
            const cleanDue = providedDue.replace(/[()]/g, '').trim();
            const isoMatch = cleanDue.match(/\d{4}-\d{2}-\d{2}/);
            if (isoMatch) parsedDue = isoMatch[0];
            else {
              const dm = cleanDue.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
              if (dm) {
                let d = Number(dm[1]), m = Number(dm[2]), y = Number(dm[3]);
                if (y < 100) y += (y >= 70 ? 1900 : 2000);
                try {
                  const dt = new Date(y, m - 1, d);
                  if (!Number.isNaN(dt.getTime())) parsedDue = dt.toISOString().slice(0, 10);
                } catch { parsedDue = null; }
              }
            }
          }
          item._parsedDue = parsedDue || null;

          const providedStart = item.startDate ? String(item.startDate).trim() : null;
          let parsedStart = null;
          if (providedStart) {
            // Strip parentheses and whitespace for cleaner parsing
            const cleanStart = providedStart.replace(/[()]/g, '').trim();
            const isoMatch2 = cleanStart.match(/\d{4}-\d{2}-\d{2}/);
            if (isoMatch2) parsedStart = isoMatch2[0];
            else {
              const dm2 = cleanStart.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
              if (dm2) {
                let d = Number(dm2[1]), m = Number(dm2[2]), y = Number(dm2[3]);
                if (y < 100) y += (y >= 70 ? 1900 : 2000);
                try {
                  const dt = new Date(y, m - 1, d);
                  if (!Number.isNaN(dt.getTime())) parsedStart = dt.toISOString().slice(0, 10);
                } catch { parsedStart = null; }
              }
            }
          }
          item._parsedStart = parsedStart || null;

          item._rawWeight = (item.weight !== undefined && item.weight !== null && Number.isFinite(Number(item.weight))) ? clampInt(Number(item.weight)) : null;
          item._originalPriority = (item.priority && typeof item.priority === 'string') ? String(item.priority).toLowerCase() : null;
          return item;
        });

        // compute weights and normalize
        const rawWeights = intermediate.map((it) => {
          if (it._rawWeight !== null) return it._rawWeight;
          const inferred = inferWeight(it, globalKeywords);
          return inferred !== null ? Math.max(5, Math.round(inferred / 5) * 5) : 5;
        });
        const sumRaw = rawWeights.reduce((s, v) => s + (Number.isFinite(Number(v)) ? Number(v) : 0), 0);
        let normalized = [];
        if (sumRaw === 0) normalized = normalizeToTargetSum(rawWeights.map(() => 1), 100);
        else normalized = normalizeToTargetSum(rawWeights, 100);
        const preferredStep = 5;
        normalized = roundToStepWithSumNormalized(normalized, 100, preferredStep);
        if ((normalized.reduce((a, b) => a + b, 0)) !== 100) {
          normalized = normalizeToTargetSum(normalized, 100);
          normalized = roundToStepWithSumNormalized(normalized, 100, preferredStep);
        }

        // finalization: dates/priority and assignee pool building
        // (Simplified assignee logic: we re-run this inside useApplyOCR anyway, so we just prep the items)
        // Detect repeated assignees, etc.

        
        // Build orderedPool logic is complex and relies on specific loop context. 
        // For brevity and robustness, we'll simplify here and let useApplyOCR do heavy lifting 
        // OR we duplicate the assignee resolver logic. 
        // The original code did assignee resolution here. We should keep doing it to provide "suggested" assignments.

        // ... [Re-using similar logic for assignee pool] ...
        // To avoid massive code duplication, we will apply basic normalization here and let useApplyOCR refine it.
        // We really just need to return the 'finalized' items with weights and basic fields.

        const finalized = intermediate.map((it, idx) => {
          const item = { ...it };
          let w = clampInt(normalized[idx] ?? 0);
          item.weight = (w === null || w === undefined) ? null : Number(w);

          // Date finalization
          let dueIso = item._parsedDue || null;
           if (!dueIso) {
            const inferred = inferDueDateFromLabels(item.labels || [], parsed.strings || '');
            if (inferred) dueIso = inferred;
          }
          let dueDateObj = dueIso ? new Date(dueIso) : null;
          let startIso = item._parsedStart || null;
          let startDateObj = startIso ? new Date(startIso) : null;

          if (dueDateObj && startDateObj) {
            const sd = new Date(startDateObj); sd.setHours(0, 0, 0, 0);
            const dd = new Date(dueDateObj); dd.setHours(0, 0, 0, 0);
            if (sd.getTime() === dd.getTime()) { dd.setDate(dd.getDate() + 1); dueDateObj = dd; }
            if (dueDateObj && (new Date(dueDateObj).getTime() < new Date(startDateObj).getTime())) {
              const newDue = new Date(startDateObj); newDue.setDate(newDue.getDate() + 1); dueDateObj = newDue;
            }
          }
          
          const toIso = (d) => { if (!d) return null; try { const dt = new Date(d); if (Number.isNaN(dt.getTime())) return null; return dt.toISOString().slice(0, 10); } catch { return null; } };
          item.startDate = toIso(startDateObj);
          item.dueDate = toIso(dueDateObj);

          item.priority = normalizePriority(item, globalKeywords) || (item._originalPriority || null);
          item.weight = (item.weight === null || item.weight === undefined) ? null : clampInt(item.weight);

          delete item._rawWeight;
          delete item._parsedDue;
          delete item._parsedStart;
          delete item._originalPriority;

          // Assignee normalization (basic)
          item.assignees = Array.isArray(item.assignees) 
            ? item.assignees.map(x => normalizeTokenForEmail(x)).filter(Boolean) 
            : [];
            
          return item;
        });

        if (finalized.length === 1) finalized[0].weight = 100;

        return {
            name: listObj.name || "Imported List",
            items: finalized
        };
    });

    parsed.lists = processedLists;
    delete parsed.arrays; // Clean up old structure
    parsed.designSuggestions = Array.isArray(parsed.designSuggestions) ? parsed.designSuggestions : [];
    parsed.uploadPrompt = parsed.uploadPrompt ?? null;

    return parsed;
  } catch (err) {
    console.error('generateContent failed', err);
    throw err;
  }
}

export async function generateBoardLayout(promptText, candidateEmails = []) {
  if (!promptText) throw new Error("No prompt provided.");

  const schema = `
You are a world-class agile project manager and productivity expert. Your task is to generate a highly focused, professional, and practical project Board structure based on the user's prompt.

Crucial Instructions:
1. Avoid generic, bloated, or boilerplate lists and tasks (e.g., don't dump generic environment setup tasks unless the prompt is specifically about setting up a repository).
2. Deeply analyze the exact context, domain (whether software engineering, marketing, business, creative writing, health, cooking, etc.), and goals in the prompt. Generate highly specific, context-appropriate, and actionable tasks.
3. Keep the board focused: typically 2 to 4 lists, and 2 to 4 highly relevant, high-value cards per list. Quality and specificity over quantity.
4. For each card, write a realistic description detailing the exact actions/goals.
5. The prompt can cover any domain. Customize code snippets, tables, checklists, or steps to match the requested domain (e.g., a recipe checklist for cooking, a weight table for exercise, or a javascript code snippet for programming).

Formatting Task Descriptions:
The application's task description renderer supports rich markdown formatting. Leverage these formats inside the "description" field where appropriate to make tasks clear, actionable, and context-rich:
- **Rich Text**: Use bold (**bold**), italic (*italic*), and inline code (\`code\`).
- **Fenced Code Blocks**: Use standard triple-backtick markdown blocks to provide templates or snippets (code, config files, formulas, etc.).
- **Markdown Tables**: Use standard tables to list parameters, uniforms, workout plans, ingredient counts, or data.
- **Smart Checklists**: You can include checkboxes in descriptions using "- [ ] Text".
  * For tasks requiring embedded code or scripts, append "(/code)" to the checkbox line and write the code block on subsequent lines indented by 4 spaces.
  * For tasks requiring embedded tables, append "(/table)" to the checkbox line and write the markdown table on subsequent lines indented by 4 spaces.

Subtasks:
Provide 2 to 5 clear, actionable subtasks per card. Do NOT write the "<!-- subtask:X -->" comments inside the subtasks list; the application automatically parses and injects these. Just focus on generating the checklist in the description and standard text items in the "subtasks" array.

Return ONLY valid JSON format. Do NOT wrap in markdown fences.
Schema required:
{
  "boardName": "String (A catchy, specific name for the board)",
  "lists": [
    {
      "name": "String (Specific list name, e.g., TSL Setup, Shader Animation)",
      "cards": [
        {
          "title": "String (Specific, actionable task title)",
          "description": "String (Detailed, context-aware description of the work utilizing rich Markdown, tables, code blocks, or checklist smart tokens where appropriate)",
          "priorityScale": "String (easy, medium, hard, high)",
          "effort": "Number (1-10 estimate)",
          "assignees": ["String (Select optionally 1-2 emails from CANDIDATE_EMAILS, or empty array)"],
          "subtasks": [
            {
              "text": "String (Actionable subtask description)",
              "weight": "Number (Integer weight. Ensure weights for all subtasks in a card sum exactly to 100)"
            }
          ]
        }
      ]
    }
  ]
}
`;

  const candidateBlock = candidateEmails.length > 0 
    ? `\nCANDIDATE_EMAILS (Use these exact emails to allocate tasks where it makes sense):\n${candidateEmails.join('\n')}\n`
    : '';

  const finalPrompt = schema + candidateBlock + `\nUSER PROMPT:\n"${promptText}"`;

  try {
    const result = await model.generateContent([{ text: finalPrompt }], { generationConfig: { responseMimeType: 'application/json' } });
    let rawText = null;
    if (typeof result.response?.text === 'function') {
        rawText = await result.response.text();
    } else if (result.response?.candidates?.length > 0) {
        rawText = result.response.candidates[0]?.content?.parts?.[0]?.text;
    }
    
    if (!rawText) throw new Error("Empty response from AI");

    let parsed = parseLooseJSON(rawText);
    if (!parsed) {
        const fenceMatch = rawText.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
        if (fenceMatch) parsed = parseLooseJSON(fenceMatch[1]);
    }
    if (!parsed) throw new Error("Failed to parse AI JSON response.");
    return parsed;
  } catch(e) {
    console.error("Board generation failed", e);
    throw e;
  }
}

export async function suggestTaskDates(title, description, priority) {
  const prompt = `
Given the following task details, suggest a realistic start date and due date.
Consider the complexity of the task based on its title and description. 
Assume today is ${new Date().toISOString().split('T')[0]}.
The priority is ${priority}.

Task Title: ${title || "Untitled Task"}
Description: ${description || "No description"}

Respond ONLY with a valid JSON object matching this schema, with NO markdown formatting:
{
  "startDate": "YYYY-MM-DD",
  "dueDate": "YYYY-MM-DD"
}
`;

  try {
    const result = await model.generateContent([{ text: prompt }], { generationConfig: { responseMimeType: 'application/json' } });
    let rawText = null;
    if (typeof result.response?.text === 'function') {
        rawText = await result.response.text();
    } else if (result.response?.candidates?.length > 0) {
        rawText = result.response.candidates[0]?.content?.parts?.[0]?.text;
    }
    
    if (!rawText) throw new Error("Empty response from AI");

    let parsed = parseLooseJSON(rawText);
    if (!parsed) {
        const fenceMatch = rawText.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
        if (fenceMatch) parsed = parseLooseJSON(fenceMatch[1]);
    }
    if (!parsed || (!parsed.startDate && !parsed.dueDate)) {
        throw new Error("Failed to parse expected AI JSON response.");
    }
    return parsed;
  } catch(e) {
    console.error("Task date suggestion failed", e);
    throw e;
  }
}
